import React, { useEffect, useState } from 'react';
import { ToastContainer, toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { Modal, Button, Spinner } from 'react-bootstrap';
import AuthenticateService from './AuthenticateService';
import Sidebar from './Sidebar';
import Header from './Header';
import TransactionHistory from './extra/dashboard/TransactionHistory';
import Footer from './Footer';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faEyeSlash } from '@fortawesome/free-solid-svg-icons';
import Swal from 'sweetalert2';
import PreLoader from './extra/PreLoader';

const Airtime = () => {
  const { http, user } = AuthenticateService();
  const { token, logout } = AuthenticateService();

  // Account selection state
  const [accounts, setAccounts] = useState([]);
  const [selectedAccountId, setSelectedAccountId] = useState('');
  const [selectedAccountIndex, setSelectedAccountIndex] = useState(0);
  const [isLoadingAccounts, setIsLoadingAccounts] = useState(false);

  // Existing state
  const [networkProvider, setNetworkProvider] = useState('');
  const [phoneNumber, setPhoneNumber] = useState('');
  const [amount, setAmount] = useState('');
  const [transactionPin, setTransactionPin] = useState('');
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isBuying, setIsBuying] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const defaultAmount = [50, 100, 200, 300, 500, 750];

  // API configuration with Authorization header
  const apiConfig = {
    headers: {
      'Authorization': `Bearer ${token}`,
      'Content-Type': 'application/json'
    }
  };

  useEffect(() => {
    document.title = user.first_name + " " + user.last_name + "- Airtime";
    getUserAccounts(); // Fetch accounts on component mount
  }, []);

  // Fetch user accounts
  const getUserAccounts = async () => {
    try {
      setIsLoadingAccounts(true);
      const response = await http.get('/users/get-details', apiConfig);

      if (response.data.status === true || response.data.status === 1) {
        const accountsData = response.data.data?.accounts || response.data.message?.data?.accounts || [];
        setAccounts(accountsData);

        if (accountsData.length > 0) {
          setSelectedAccountId(accountsData[0].id);
          setSelectedAccountIndex(0);
        }
      }
    } catch (error) {
      console.error('Error fetching accounts:', error);
      if (error.response?.status === 401) {
        logout();
      }
    } finally {
      setIsLoadingAccounts(false);
    }
  };

  // Handle account selection
  const handleAccountSelect = (index, account) => {
    setSelectedAccountIndex(index);
    setSelectedAccountId(account.id);
  };

  // Accounts Ribbon Component
  const AccountsRibbon = () => {
    if (isLoadingAccounts) {
      return (
        <div className="text-center py-3 mb-4">
          <Spinner animation="border" size="sm" />
          <span className="ms-2">Loading accounts...</span>
        </div>
      );
    }

    if (accounts.length === 0) {
      return (
        <div className="alert alert-warning mb-4">
          No accounts found. Please add an account first.
        </div>
      );
    }

    return (
      <div className="accounts-ribbon mb-4">
        <h6 className="mb-2">Select Source Account</h6>
        <div className="d-flex overflow-auto pb-2" style={{ gap: '10px' }}>
          {accounts.map((account, index) => (
            <div
              key={account.id}
              className={`account-card ${selectedAccountIndex === index ? 'selected' : ''}`}
              onClick={() => handleAccountSelect(index, account)}
              style={{ cursor: 'pointer', minWidth: '220px' }}
            >
              <div className="p-3 rounded" style={{
                backgroundColor: selectedAccountIndex === index ? '#351880' : '#f8f9fa',
                border: `1px solid ${selectedAccountIndex === index ? '#351880' : '#dee2e6'}`
              }}>
                <div className="d-flex justify-content-between align-items-start">
                  <div>
                    <div className="text-truncate" style={{
                      color: selectedAccountIndex === index ? '#fff' : '#212529',
                      fontWeight: '500',
                      fontSize: '0.875rem'
                    }}>
                      {account.account_type?.savings_product || 'Account'}
                    </div>
                    <div className="mt-1" style={{
                      color: selectedAccountIndex === index ? '#fff' : '#212529',
                      fontWeight: 'bold',
                      fontSize: '1rem'
                    }}>
                      ₦{account.user_wallet?.balance || '0.00'}
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <div className="x-small" style={{
                    color: selectedAccountIndex === index ? 'rgba(255,255,255,0.8)' : '#6c757d'
                  }}>
                    A/C: {account.virtual_accnum || 'N/A'}
                  </div>
                  <div className="x-small text-truncate" style={{
                    color: selectedAccountIndex === index ? 'rgba(255,255,255,0.7)' : '#6c757d'
                  }}>
                    {account.bank?.bank_name || 'Bank'}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const validateForm = () => {
    if (!networkProvider || !phoneNumber) {
      toast.error('Please fill in all fields');
      return false;
    }
    if (!selectedAccountId) {
      toast.error('Please select a source account');
      return false;
    }
    return true;
  };

  const handleSubmit = (event) => {
    event.preventDefault();
    if (validateForm()) {
      setShowPasswordModal(true);
    }
  };

  const handleContinue = async () => {
    if (validateForm()) {
      try {
        setIsBuying(true);
        const response = await http.post('/transactions/buy-airtime', {
          final_amount: amount,
          transaction_type: 11,
          network_provider: networkProvider,
          phone_number: phoneNumber,
          transaction_pin: transactionPin,
          description: networkProvider + " " + phoneNumber + " " + amount,
          selectedAccountId: selectedAccountId
        }, apiConfig).then((res) => {
          if (res.data.status === true) {
            setNetworkProvider('');
            setPhoneNumber('');
            setAmount('');
            setTransactionPin('');
            setShowPasswordModal(false);
            setIsBuying(false);
            Swal.fire({
              title: 'Successful',
              text: res.data.message,
              icon: 'success',
              confirmButtonText: 'OK',
            });
          } else {
            Swal.fire({
              title: 'Failed',
              text: res.data.message || 'Airtime purchase failed',
              icon: 'error',
              confirmButtonText: 'OK',
            });
            setIsBuying(false);
          }
        }).catch((error) => {
          setIsBuying(false);
          Swal.fire({
            title: 'Failed',
            text: error?.response?.data?.message || 'Airtime purchase failed',
            icon: 'error',
            confirmButtonText: 'OK',
          });
        });
      } catch (error) {
        setIsBuying(false);
        if (error.response?.status === 401) {
          logout();
        }
        Swal.fire({
          title: 'Failed',
          text: error?.response?.data?.message || 'Airtime purchase failed',
          icon: 'error',
          confirmButtonText: 'OK',
        });
      }
    }
  };

  // CSS styles for accounts ribbon
  const accountsStyles = `
    .account-card {
      transition: all 0.2s ease;
    }

    .account-card:hover {
      transform: translateY(-2px);
      box-shadow: 0 4px 12px rgba(0,0,0,0.1);
    }

    .account-card.selected {
      box-shadow: 0 4px 12px rgba(53, 24, 128, 0.2);
    }

    .x-small {
      font-size: 0.75rem;
    }

    .accounts-ribbon {
      background: white;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #dee2e6;
    }

    @media (max-width: 768px) {
      .account-card {
        min-width: 200px !important;
      }
    }
  `;

  return (
    <>
      <style>{accountsStyles}</style>

      {isLoading ? (
        <PreLoader />
      ) : (
        <>
          <Sidebar />
          <Header user={user} />

          <div className="content">
            <form className="mb-9" onSubmit={handleSubmit}>
              <div className="row g-5">
                <div className="col-xl-8">
                  <h4 className="fs-1 mb-4">Airtime Subscription</h4>

                  {/* Accounts Ribbon */}
                  <AccountsRibbon />

                  <div className="row gx-3 gy-4">
                    <div className="col-sm-6 col-md-6">
                      <div className="form-floating">
                        <select
                          className="form-select"
                          id="floatingSelectTask"
                          value={networkProvider}
                          onChange={(e) => setNetworkProvider(e.target.value)}
                          required
                        >
                          <option value="">Select Network</option>
                          <option value="mtn">MTN</option>
                          <option value="glo">Glo</option>
                          <option value="airtel">Airtel</option>
                          <option value="etisalat">Etisalat</option>
                        </select>
                        <label htmlFor="floatingSelectTask">Network Provider</label>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="floatingEventInput"
                          type="text"
                          placeholder="Phone Number"
                          value={phoneNumber}
                          onChange={(e) => setPhoneNumber(e.target.value)}
                          required
                        />
                        <label htmlFor="floatingEventInput">Phone Number</label>
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6">
                      <div className="row">
                        {defaultAmount.map((amt, index) => (
                          <div key={index} className="col-4">
                            <span
                              onClick={() => setAmount(amt)}
                              className={`btn btn-sm rounded-pill w-100 me-3 mb-2 fs--1 fs-sm-0 ${amount === amt ? 'btn-warning' : 'btn-outline-warning'}`}
                            >
                              {amt}
                            </span>
                          </div>
                        ))}
                      </div>
                    </div>

                    <div className="col-sm-6 col-md-6">
                      <div className="form-floating">
                        <input
                          className="form-control"
                          id="floatingEventInput"
                          type="number"
                          step={2}
                          placeholder="Amount"
                          value={amount}
                          onChange={(e) => setAmount(e.target.value)}
                          required
                        />
                        <label htmlFor="floatingEventInput">Amount</label>
                      </div>
                    </div>

                    <div className="col-12 col-sm-auto col-xl-4">
                      <div className="d-flex">
                        <button
                          className="btn btn-primary px-5 w-100 text-nowrap"
                          type="submit"
                          disabled={!selectedAccountId}
                        >
                          Continue
                        </button>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </form>

            <TransactionHistory transaction_type={11} title="Airtime Transactions" />
            <Footer />
          </div>

          <Modal show={showPasswordModal} onHide={() => setShowPasswordModal(false)} backdrop="static">
            <Modal.Header closeButton>
              <Modal.Title>Enter Transaction PIN</Modal.Title>
            </Modal.Header>
            <Modal.Body>
              <div className="form-floating">
                <input
                  className="form-control"
                  type={showPassword ? 'number' : 'password'}
                  maxLength={4}
                  placeholder="Transaction Pin"
                  value={transactionPin}
                  onChange={(e) => setTransactionPin(e.target.value)}
                  required
                />
                <button
                  type="button"
                  style={{
                    position: 'absolute',
                    top: '50%',
                    right: '10px',
                    transform: 'translateY(-50%)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                  }}
                  className="btn btn-link password-toggle"
                  onClick={() => setShowPassword(!showPassword)}
                >
                  <FontAwesomeIcon icon={showPassword ? faEyeSlash : faEye} />
                </button>
                <label htmlFor="floatingEventInput">Transaction PIN</label>
              </div>
            </Modal.Body>
            <Modal.Footer>
              <Button variant="secondary" onClick={() => setShowPasswordModal(false)}>
                Cancel
              </Button>
              <Button variant="primary" onClick={handleContinue} disabled={isBuying}>
                {isBuying ? (
                  <>
                    <Spinner animation="border" size="sm" /> Loading...
                  </>
                ) : (
                  "Buy Airtime"
                )}
              </Button>
            </Modal.Footer>
          </Modal>

          <ToastContainer />
        </>
      )}
    </>
  );
}

export default Airtime;